// Libraries
import { ReactElement } from 'react';
import { DomainPaddingPropType, DomainPropType, OrientationTypes, Padding } from 'victory';
// Types
import { ChartLineStyles, RatingKeys, RatingRanges } from '@globalTypes/index';
import { VictoryAreas, VictoryPointScatters, VictoryLines } from './Components/index';

/**
 * Represents a point on the chart.
 */
export type Point = {
  /**
   * The x-coordinate of the point.
   */
  x: number | string;
  /**
   * The y-coordinate of the point.
   */
  y: number | string;
};

/**
 * Represents a point area on the chart.
 */
export type PointArea = Point & {
  /**
   * The y-coordinate of the point.
   */
  y0: number;
};

/**
 * Represents keys of the LineVariation enum.
 */
export type LineVariationKeys = keyof typeof ChartLineStyles;

/**
 * Represents Scatter styles apart of RatingKeys.
 */
export enum ScatterPointStyles {
  yellow = 'yellow',
  blue = 'blue',
}

/**
 * The keys of each style for the Scatter points
 */
export type ScatterPointKeys = keyof typeof ScatterPointStyles;

/**
 * Represents keys of the LineVariation enum.
 */
export type ScatterPointVariationKeys = RatingKeys | ScatterPointKeys;

/**
 * Represents labels for the chart.
 */
export type ChartLabels = {
  /**
   * Optional x-axis label.
   */
  xLabel?: string;
  /**
   * Optional y-axis label.
   */
  yLabel?: string;
};

/**
 * Represents tick counts for the chart.
 */
export type TickCounts = {
  /**
   * Optional x-axis tick count.
   */
  xTickCount?: number;
  /**
   * Optional y-axis tick count.
   */
  yTickCount?: number;
};

/**
 * Represents data formatters for the chart data.
 */
export type DataFormatters = {
  /**
   * Optional function to format x-axis data.
   */
  xDataFormatter?: (args: unknown) => string;
  /**
   * Optional function to format y-axis data.
   */
  yDataFormatter?: (args: unknown) => string;
};

/**
 * Represents tick formatters for the chart ticks.
 */
export type TickFormatters = {
  /**
   * Optional function to format x-axis ticks.
   */
  xTickFormatter?: ((...args: number[]) => string) | string[];
  /**
   * Optional function to format y-axis ticks.
   */
  yTickFormatter?: ((...args: number[]) => string) | string[];
};

/**
 * Represents tick values for the chart ticks.
 */
export type TickValues = {
  /**
   * Optional values for the x-axis ticks.
   */
  xTickValues?: string[] | number[] | Date[];
  /**
   * Optional values for the y-axis ticks.
   */
  yTickValues?: string[] | number[];
};

/**
 * Represents tick formatters for the chart ticks.
 */
export type InvertAxes = {
  /**
   * Optional to invert the chart y axis if true
   */
  invertYAxis?: boolean;
  /**
   * Optional to invert the chart x axis if true
   */
  invertXAxis?: boolean;
};

/**
 * Represents additional axis settings for the chart.
 */
type AdditionalAxis = {
  /**
   * Determines whether the axis is dependent (true) or independent (false).
   * If dependent, it will render on the left side for the y-axis or at the bottom for the x-axis.
   * If independent, it will render on the right side for the y-axis or at the top for the x-axis.
   */
  dependentAxis: boolean;
  /**
   * An array of tick values to be displayed on the axis.
   */
  tickValues: number[];
  /**
   * An array of string formats to customize the tick labels on the axis.
   * The order of the formats corresponds to the order of the tickValues.
   */
  tickFormat: string[];
  /**
   * Specifies the orientation of the axis, whether it's vertical ('vertical') or horizontal ('horizontal').
   */
  orientation: OrientationTypes;
};

/**
 * Represents props for the LineChart component.
 */
export type LineChartProps = {
  /**
   * Array of lines to be rendered on the chart.
   */
  victoryLines: VictoryLines;
  /**
   * Tooltip component.
   */
  tooltip: ReactElement;
  /**
   * Additional axis settings for the chart.
   */
  additionalAxis?: AdditionalAxis;
  /**
   * Array of areas to be rendered on the chart.
   */
  victoryAreas?: VictoryAreas;
  /**
   * Array of point scatters to be rendered on the chart.
   */
  victoryPointScatters?: VictoryPointScatters;
  /**
   * Optional chart labels.
   */
  labels?: ChartLabels;
  /**
   * Optional tick counts.
   */
  tickCounts?: TickCounts;
  /**
   * Optional tick formatters.
   */
  tickFormatters?: TickFormatters;
  /**
   * Optional tick values.
   */
  tickValues?: TickValues;
  /**
   * Domain padding for the chart.
   */
  domainPadding?: DomainPaddingPropType;
  /**
   * Rating ranges for gradients.
   */
  ratingRanges?: RatingRanges;
  /**
   * Determines whether to show the grey axis.
   */
  showGreyAxis?: boolean;
  /**
   * List of data keys to be blacklisted in the tooltip.
   */
  tooltipBlacklist?: (string | RegExp)[];
  /**
   * The limit of the chart for the x axis.
   */
  xLimit?: number | string;
  /**
   * Optional for inverting the axes.
   */
  invertAxes?: InvertAxes;

  /**
   * Optional for padding the full chart.
   */
  padding?: number | Padding;

  /**
   * The domain of the chart.
   */
  domain?: DomainPropType;
};

/**
 * The line types, solid is ''or dashed, otherwise.
 */
export enum LineType {
  Dashed = 'Dashed',
}

export type Scores = {
  cii: number;
  time: string;
  upperCII: number;
  lowerCII: number;
};
[];
